import { TrendingUp, TrendingDown, Zap, Users, DollarSign, Calendar, Target, Leaf, Package, Handshake, UserCog, FileText } from "lucide-react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"

interface MetricCardProps {
  title: string
  value: string | number
  description: string
  trend?: "up" | "down" | "neutral"
  trendValue?: string
  icon: React.ElementType
  color?: "primary" | "accent" | "success" | "warning" | "info"
}

function MetricCard({ title, value, description, trend, trendValue, icon: Icon, color = "primary" }: MetricCardProps) {
  const colorClasses = {
    primary: "bg-primary/10 text-primary",
    accent: "bg-accent/10 text-accent-foreground",
    success: "bg-success/10 text-success",
    warning: "bg-warning/10 text-warning-foreground",
    info: "bg-blue-500/10 text-blue-600"
  }

  return (
    <Card className="relative overflow-hidden border-0 shadow-soft hover:shadow-medium transition-all duration-200">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          <Icon className="h-4 w-4" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <div>
            <div className="text-2xl font-bold text-foreground">{value}</div>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
          {trend && trendValue && (
            <div className="flex items-center gap-1">
              {trend === "up" && <TrendingUp className="h-4 w-4 text-success" />}
              {trend === "down" && <TrendingDown className="h-4 w-4 text-destructive" />}
              <Badge 
                variant={trend === "up" ? "default" : trend === "down" ? "destructive" : "secondary"}
                className="text-xs"
              >
                {trendValue}
              </Badge>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

interface MetricsGridProps {
  metrics?: {
    totalPlacas?: number;
    potenciaInstalada?: number;
    contratosFechados?: number;
    receitaTotal?: number;
    lucroLiquido?: number;
    novosLeads?: number;
    taxaConversao?: number;
    projetosAtivos?: number;
    lucroPorPlaca?: number;
    leadsCapturados?: number;
    totalParceiros?: number;
    totalVendedores?: number;
    totalPropostas?: number;
    valorTotalPropostas?: number;
  };
  exclude?: string[];
}

export function MetricsGrid({ metrics, exclude = [] }: MetricsGridProps) {
  const formatMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(valor);
  };

  const defaultMetrics: MetricCardProps[] = [
    {
      title: "Placas Instaladas",
      value: metrics?.totalPlacas?.toLocaleString('pt-BR') || "0",
      description: "Total histórico",
      icon: Package,
      color: "primary"
    },
    {
      title: "Potência Instalada",
      value: `${metrics?.potenciaInstalada?.toFixed(2) || "0"} kWp`,
      description: "Capacidade total",
      icon: Zap,
      color: "warning"
    },
    {
      title: "Contratos Fechados",
      value: metrics?.contratosFechados?.toString() || "0",
      description: "Vendas concluídas",
      icon: Target,
      color: "success"
    },
    {
      title: "Receita Total",
      value: formatMoeda(metrics?.receitaTotal || 0),
      description: "Volume de contratos",
      icon: DollarSign,
      color: "primary"
    },
    {
      title: "Lucro por Placa",
      value: formatMoeda(metrics?.lucroPorPlaca || 0),
      description: "Média por painel",
      icon: TrendingUp,
      color: "success"
    },
    {
      title: "Taxa de Conversão",
      value: `${metrics?.taxaConversao?.toFixed(1) || "0"}%`,
      description: "Leads para Contratos",
      trend: (metrics?.taxaConversao || 0) > 20 ? "up" : "neutral",
      trendValue: (metrics?.taxaConversao || 0) > 20 ? "+2.1%" : undefined,
      icon: Users,
      color: "accent"
    },
    {
      title: "Leads Capturados",
      value: metrics?.leadsCapturados?.toLocaleString('pt-BR') || "0",
      description: "Total de leads",
      icon: Users,
      color: "info"
    },
    {
      title: "Parceiros Cadastrados",
      value: metrics?.totalParceiros?.toLocaleString('pt-BR') || "0",
      description: "Total de parceiros",
      icon: Handshake,
      color: "accent"
    },
    {
      title: "Vendedores Cadastrados",
      value: metrics?.totalVendedores?.toLocaleString('pt-BR') || "0",
      description: "Total de vendedores",
      icon: UserCog,
      color: "primary"
    },
    {
      title: "Propostas Geradas",
      value: metrics?.totalPropostas?.toLocaleString('pt-BR') || "0",
      description: "Leads com proposta",
      icon: FileText,
      color: "info"
    },
    {
      title: "Valor Total Propostas",
      value: formatMoeda(metrics?.valorTotalPropostas || 0),
      description: "Soma das propostas",
      icon: DollarSign,
      color: "success"
    }
  ]

  const filteredMetrics = defaultMetrics.filter(metric => !exclude.includes(metric.title));

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
      {filteredMetrics.map((metric, index) => (
        <MetricCard key={index} {...metric} />
      ))}
    </div>
  )
}
